import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Box, Button, Stack, TextField, Typography} from 'views/components';

import {DateTimePicker} from '../dates';
import {Translate, translate} from '../Translate';
import TaskDescriptionField from './TaskDescriptionField';

import * as chrono from 'chrono-node';
import moment from 'moment';

const TaskModal = (props) => {
  const {
    mode = 'add',
    onClose,
    addTask,
    editTask,
    task,
    clientRn = '',
    clientName = '',
    caregiverRn = '',
    caregiverName = '',
    user,
  } = props;

  const isEditMode = mode === 'edit';

  const [taskTitle, setTaskTitle] = React.useState(task?.title || '');
  const [taskDescription, setTaskDescription] = React.useState(task?.description?.blob || '');
  const [taskDueDate, setTaskDueDate] = React.useState(() => {
    const parsedDueDate = moment(task?.dueDate || moment().hour(23).minute(59).second(0).millisecond(0));

    if (!parsedDueDate.isValid()) {
      return '';
    }

    return parsedDueDate.local().format('YYYY-MM-DDTHH:mm');
  });
  const [type, setType] = React.useState(isEditMode ? task?.type || null : 'CALL');
  const [connections, setConnections] = React.useState(() => {
    if (isEditMode) {
      return task?.connections || [];
    }

    if (clientRn) {
      return [{RN: clientRn, name: clientName || clientRn, type: 'client'}];
    }

    if (caregiverRn) {
      return [{RN: caregiverRn, name: caregiverName || caregiverRn, type: 'caregiver'}];
    }

    return [];
  });
  const [owners, setOwners] = React.useState(isEditMode ? [] : [user]);

  const formatDueDateForInput = (dueDate) => {
    if (!dueDate) {
      return '';
    }

    const parsedDueDate = moment(dueDate);

    if (!parsedDueDate.isValid()) {
      return '';
    }

    return parsedDueDate.local().format('YYYY-MM-DDTHH:mm');
  };

  const getEndOfDayMoment = (inputMoment = moment()) => {
    return moment(inputMoment).hour(23).minute(59).second(0).millisecond(0);
  };

  const parseDueDateFromDescription = (value) => {
    if (!value.trim()) {
      return null;
    }

    const referenceDate = moment().startOf('day').toDate();
    const parsedResult = chrono.parse(value, referenceDate, {forwardDate: true})[0];

    if (!parsedResult) {
      return null;
    }

    const hasExplicitTime = parsedResult.start.isCertain('hour') || parsedResult.start.isCertain('minute');
    const parsedDueDate = moment(parsedResult.start.date());
    const resolvedDueDate = hasExplicitTime ?
      parsedDueDate.second(0).millisecond(0) :
      getEndOfDayMoment(parsedDueDate);

    return formatDueDateForInput(resolvedDueDate);
  };

  const getDescriptionForDateParsing = (value) => {
    return value
        .split('\n')
        .filter((line) => {
          const trimmedLine = line.trimStart();
          return !['@', '#', '$', '!'].includes(trimmedLine[0]);
        })
        .join('\n');
  };

  const formatDueDateForSubmit = (dueDate) => {
    const parsedDueDate = moment(dueDate);

    if (!parsedDueDate.isValid()) {
      return '';
    }

    return parsedDueDate.utc().toISOString();
  };

  const syncDueDateFromDescription = (value) => {
    const parsedDueDate = parseDueDateFromDescription(getDescriptionForDateParsing(value));
    if (parsedDueDate) {
      setTaskDueDate(parsedDueDate);
    }
  };

  const updateTaskDescription = (value) => {
    const nextDescription = value ?? '';

    setTaskDescription(nextDescription);
    syncDueDateFromDescription(nextDescription);
  };

  const handleSubmitTask = () => {
    const payload = {
      title: taskTitle.trim(),
      description: {
        blob: taskDescription.trim(),
        editorType: 'MARKDOWN',
      },
      dueDate: formatDueDateForSubmit(taskDueDate),

      ownerRns: owners.map((owner) => owner.userRn),
      connections: connections.map((connection) => connection.RN),
    };

    if (type !== undefined) {
      payload.type = type;
    }

    if (isEditMode) {
      editTask(task.taskRn, payload);
      return;
    }

    addTask(payload);
  };

  const commands = ['assign', 'caregiver', 'client', 'type'];

  return (
    <Dialog
      onClose={onClose}
      open={true}
      maxWidth="md"
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '720px',
          height: 'fit-content',
          maxHeight: '90vh',
          borderRadius: '8px',
        },
      }}
    >
      <DialogTitle
        onClose={onClose}
        closeButtonSx={{
          border: '1px solid #E8E4DE',
          borderRadius: '8px',
          color: '#A8A49E',
          p: '6px',
        }}
        closeIconSx={{
          fontSize: '18px',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent='space-between'
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
          >
            <Typography variant="h6">
              <Translate expression={`client.ClientDetail.Tasks.Type.${isEditMode ? 'EditTask' : 'AddTask'}.Title`} />
            </Typography>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" sx={{fontWeight: 500}}>
              <Translate expression={'client.ClientDetail.Tasks.Type.Common.TitleField'} />
              <span style={{color: 'red'}}> *</span>
            </Typography>
            <TextField
              variant='outlined'
              placeholder={translate('client.ClientDetail.Tasks.Type.Common.TitlePlaceholder')}
              value={taskTitle}
              onChange={setTaskTitle}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{fontWeight: 500}}>
              <Translate expression='client.ClientDetail.Tasks.Type.Common.DueDateField' />
              <span style={{color: 'red'}}> *</span>
            </Typography>
            <DateTimePicker
              format='dddd MMM Do h:mmA'
              value={taskDueDate ? moment(taskDueDate) : null}
              onChange={(value) => setTaskDueDate(value?.isValid?.() ? formatDueDateForInput(value) : '')}
              textFieldProps={{
                fullWidth: true,
                sx: {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                },
              }}
            />
          </Box>

          <TaskDescriptionField
            taskDescription={taskDescription}
            updateTaskDescription={updateTaskDescription}
            type={type}
            setType={setType}
            owners={owners}
            setOwners={setOwners}
            connections={connections}
            setConnections={setConnections}
            isEditMode={isEditMode}
            task={task}
          />

          {/* Commands list */}
          <Box
            sx={{
              borderRadius: '8px',
              backgroundColor: 'background.default',
              width: '100%',
              p: 2,
            }}
          >
            <Typography variant="body2" sx={{fontWeight: 500, mb: 1}}>
              <Translate expression='client.ClientDetail.Tasks.Type.Common.Commands.Title' />
            </Typography>
            <Box sx={{display: 'flex', flex: '1 1 0%', flexDirection: 'row', justifyContent: 'space-between'}}>
              <Stack
                sx={{
                  width: '50%',
                }}
                gap={2}
              >
                {
                  commands.slice(0, Math.ceil(commands.length/2)).map((cmd) => {
                    return (
                      <Box key={cmd}>
                        <Box
                          component='span'
                          sx={{
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            py: 0.75,
                            px: 1.5,
                          }}
                        >
                          <Typography component='span' variant="body2" color='text.secondary' sx={{fontSize: 14, fontFamily: 'monospace'}}>
                            <Translate expression={`client.ClientDetail.Tasks.Type.Common.Commands.${cmd[0].toUpperCase()+cmd.slice(1)}.Cmd`} />
                          </Typography>
                        </Box>
                        <span> — </span>
                        <Typography component='span' variant="body2" color='text.secondary'>
                          <Translate expression={`client.ClientDetail.Tasks.Type.Common.Commands.${cmd[0].toUpperCase()+cmd.slice(1)}.HelperTxt`} />
                        </Typography>
                      </Box>
                    );
                  })
                }
              </Stack>
              <Stack
                sx={{
                  width: '50%',
                }}
                gap={2}
              >
                {
                  commands.slice(Math.ceil(commands.length/2)).map((cmd) => {
                    return (
                      <Box key={cmd}>
                        <Box
                          component='span'
                          sx={{
                            borderRadius: '8px',
                            backgroundColor: 'white',
                            py: 0.75,
                            px: 1.5,
                          }}
                        >
                          <Typography component='span' variant="body2" color='text.secondary' sx={{fontSize: 14, fontFamily: 'monospace'}}>
                            <Translate expression={`client.ClientDetail.Tasks.Type.Common.Commands.${cmd[0].toUpperCase()+cmd.slice(1)}.Cmd`} />
                          </Typography>
                        </Box>
                        <span> — </span>
                        <Typography component='span' variant="body2" color='text.secondary'>
                          <Translate expression={`client.ClientDetail.Tasks.Type.Common.Commands.${cmd[0].toUpperCase()+cmd.slice(1)}.HelperTxt`} />
                        </Typography>
                      </Box>
                    );
                  })
                }
              </Stack>
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          mt: 'auto',
          px: 3,
          pb: 3,
          pt: 2,
        }}
      >
        <Button
          variant='text'
          onClick={onClose}
          sx={{
            border: '1px solid #E8E4DE',
            borderRadius: '7px',
            px: 2,
            py: 1,
            color: '#DA7955',
          }}
        >
          <Translate expression='generic.Cancel' />
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmitTask}
          disabled={!taskTitle.trim() || !taskDueDate.trim()}
          sx={{
            'backgroundColor': '#DA7955',
            'borderRadius': '7px',
            'px': 2,
            'py': 1,
            '&:hover': {
              backgroundColor: '#DA7955',
            },
          }}
        >
          <Translate expression={`client.ClientDetail.Tasks.Type.${isEditMode ? 'EditTask' : 'AddTask'}.Submit`} />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

TaskModal.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit']),
  onClose: PropTypes.func.isRequired,
  addTask: PropTypes.func,
  editTask: PropTypes.func,
  task: PropTypes.object,
  clientRn: PropTypes.string,
  clientName: PropTypes.string,
  caregiverRn: PropTypes.string,
  caregiverName: PropTypes.string,
  user: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps, dispatch)(React.memo(TaskModal));
